// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { IconWrapper } from "@insite/mobius/Icon";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import { cartLineBreakpoints } from "@plumberssupply/App/Components/Cart/CartContainerLayout";
import { color, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends SelectProps {
    labelOverride?: React.ReactNode;
    disabled?: boolean;
    boxStyle?: boolean;
    extendedStyles?: SelectPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export const productUnitOfMeasureSelectStyles: SelectPresentationProps = {
    css: css`
        &&&&& {
            box-shadow: none;
            border: none;
            border-bottom: 1px solid ${color("secondary")};
            height: 18px;
            padding: 0 8px 0 0;
            min-width: 50px;
            font-size: 14px;
            line-height: 18px;
            padding-left: 8px;
            padding-right: 16px;
            width: auto;
            ${xScreen("lte", cartLineBreakpoints.md)} {
                max-width: 85px;
            }
        }
    `,
    cssOverrides: {
        formField: css`
            min-width: 50px;
            width: auto;
            ${xScreen("lte", cartLineBreakpoints.md)} {
                max-width: 85px;
            }
            margin-right: 8px;

            &&& {
                position: relative;
                &:before {
                    content: "/";
                    font-size: 14px;
                    line-height: 18px;
                    position: absolute;
                    left: 0;
                    top: 0;
                    height: 18px;
                    z-index: 1;
                }

                ${IconWrapper} {
                    &,
                    & svg {
                        width: 8px;
                        height: 18px;
                        color: ${color("common.ui9")};
                    }
                }
            }
        `,
        inputSelect: css`
            overflow: hidden;
            width: 72px;
            max-width: 72px;
        `,
    },
};

const ProductUnitOfMeasureSelect: React.FC<Props> = ({
    productContext: {
        product: { unitOfMeasures },
        productInfo: { unitOfMeasure, productId },
        onUnitOfMeasureChanged,
    },
    disabled,
    productSettings,
    labelOverride,
    boxStyle,
    extendedStyles,
    ...otherProps
}) => {
    const [styles] = React.useState(() => mergeWithCss(productUnitOfMeasureSelectStyles, extendedStyles));

    if (
        !boxStyle &&
        (!unitOfMeasures ||
            !productSettings?.alternateUnitsOfMeasure ||
            unitOfMeasures.filter(uom => uom.unitOfMeasure).length <= 1)
    ) {
        return null;
    }

    const uomChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!onUnitOfMeasureChanged) {
            logger.warn(`There was no onUnitOfMeasureChanged passed to the ProductContext for ${productId}`);
            return;
        }
        onUnitOfMeasureChanged(event.target.value);
    };

    const uomOptions = unitOfMeasures?.map(uom => (
        <option key={uom.id.toString()} value={uom.unitOfMeasure}>
            {uom.description ? `${uom.description}` : uom.unitOfMeasureDisplay}{" "}
        </option>
    ));

    return (
        <Select
            label={labelOverride ?? translate("U/M")}
            value={unitOfMeasure}
            onChange={uomChangeHandler}
            disabled={disabled}
            data-test-selector="product_unitOfMeasureSelect"
            {...(!boxStyle && { ...styles })}
            {...otherProps}
        >
            {uomOptions}
        </Select>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductUnitOfMeasureSelect));
